import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { MouseEvent, useState } from "react";

import BackButton from "@/components/BackButton";
import { PageHeading } from "@/components/Layout/Parts";
import ImageToolContentBox from "./components/ImageToolContentBox";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { ImageGenerationProjectResource } from "@/types/resources";
import ContentSpinner from "@/components/ContentSpinner";
import ImageToolQueriesBox from "./components/ImageToolQueriesBox";
import ImageToolImages from "./components/ImageToolImages";
import Button from "@/components/Button";
import ImageToolFloatingBox from "./components/ImageToolFloatingBox";
import Text from "@/components/Text";
import { ImageGenerationProjectStatus, ImageQueryType } from "@/utils/enums";
import useToasts from "@/hooks/useToasts";

const HeadingsRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ButtonsHolder = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledButton = styled(Button)`
  align-items: center;
  padding: 0 32px;
  background-color: transparent;
  border-color: ${({ theme }) => theme.colors.lightGray};
`;

const DeleteButton = styled(StyledButton)`
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.error};
  }
`;

const ActionButtonsHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const StyledBackButton = styled(BackButton)`
  margin-bottom: 0;
`;

const FavoriteButton = styled(Button)`
  color: ${({ theme }) => theme.colors.black};
  font-size: 11px;
  svg {
    transform: translateY(-1px);
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  &:active {
    border-color: ${({ theme }) => theme.colors.black};
  }
`;

const ImageToolSingle = () => {
  const { id } = useParams();

  const [selectedStockImages, setSelectedStockImages] = useState<number[]>([]);

  const [selectedAiImages, setSelectedAiImages] = useState<number[]>([]);

  const [selectedInstagramImages, setSelectedInstagramImages] = useState<
    number[]
  >([]);

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const alerts = useAlerts();

  const toasts = useToasts();

  const {
    refetch,
    isLoading,
    data: imageProject,
  } = useQuery<ImageGenerationProjectResource>(
    ["image_generation_project", { id }],
    () =>
      api.get<never, ImageGenerationProjectResource>(
        `/v1/image-generation-projects/${id}`
      ),
    {
      staleTime: 60 * 1000,
      retry: 2,
      refetchIntervalInBackground: true,
      refetchInterval: 20000,
      onError: (err) => {
        if ((err as any).response.status === 404) {
          return navigate("/404", {
            replace: true,
          });
        }
        alerts.error();
      },
    }
  );

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this project?"
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/image-generation-projects/${imageProject?.id}`
      );

      alerts.success("Project was successfully deleted");

      queryClient.invalidateQueries(["image_generation_projects"], {
        exact: false,
      });

      navigate("/general/image-tool/type/recents");
    } catch (e) {
      alerts.error();
    }
  };

  const onStartOver = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to start over this project?"
      );

      if (!confirm) return;

      await api.post<never, { id: number }>(
        `/v1/image-generation-projects/${imageProject?.id}/restart`
      );

      await queryClient.invalidateQueries(
        ["image_generation_project", { id }],
        {
          exact: true,
        }
      );

      alerts.success("Project was successfully reset");
    } catch (e) {
      alerts.error();
    }
  };

  const toggleFavorite = async () => {
    const toastId = toasts.loading();

    try {
      await api.patch<never, { id: number }>(
        `/v1/image-generation-projects/${imageProject?.id}/favourite`
      );

      await queryClient.invalidateQueries(
        ["image_generation_project", { id }],
        {
          exact: true,
        }
      );

      toasts.success(toastId);
    } catch (_) {
      toasts.error(toastId);
    }
  };

  if (isLoading || !imageProject) {
    return <ContentSpinner />;
  }

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <HeadingsRow>
        <PageHeading>Image Tool</PageHeading>
        <ButtonsHolder>
          <DeleteButton onClick={onDelete} color="secondary">
            Delete
          </DeleteButton>
          <StyledButton onClick={onStartOver} color="secondary">
            Start over
          </StyledButton>
        </ButtonsHolder>
      </HeadingsRow>
      <ActionButtonsHolder>
        <StyledBackButton link="/general/image-tool/type/recents" />
        <FavoriteButton small color="action" onClick={toggleFavorite}>
          {imageProject.isFavourite ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
            >
              <path
                d="M8.29381 11.8148L11.6562 13.8808C12.086 14.1449 12.6196 13.7521 12.4921 13.2655L11.5206 9.55934C11.4932 9.45611 11.4965 9.3475 11.5299 9.24598C11.5634 9.14446 11.6257 9.05416 11.7098 8.98547L14.7249 6.55162C15.1211 6.23183 14.9166 5.59416 14.4076 5.56212L10.47 5.31428C10.364 5.30693 10.2622 5.27052 10.1767 5.20927C10.0911 5.14803 10.0253 5.06447 9.98682 4.96834L8.51826 1.38172C8.47834 1.27969 8.40737 1.19184 8.31475 1.12985C8.22214 1.06787 8.11227 1.03467 7.99974 1.03467C7.88721 1.03467 7.77735 1.06787 7.68473 1.12985C7.59212 1.19184 7.52114 1.27969 7.48122 1.38172L6.01266 4.96834C5.97419 5.06447 5.90834 5.14803 5.8228 5.20927C5.73725 5.27052 5.63553 5.30693 5.52947 5.31428L1.59187 5.56212C1.08287 5.59416 0.878386 6.23183 1.27456 6.55162L4.28972 8.98547C4.37375 9.05416 4.43608 9.14446 4.46954 9.24598C4.503 9.3475 4.50625 9.45611 4.47889 9.55934L3.57797 12.9964C3.42492 13.5803 4.06527 14.0516 4.58102 13.7347L7.70567 11.8148C7.79355 11.7605 7.89557 11.7317 7.99974 11.7317C8.10392 11.7317 8.20593 11.7605 8.29381 11.8148Z"
                fill="#ED911B"
                stroke="#ED911B"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
            >
              <path
                d="M8.29381 11.8148L11.6562 13.8808C12.086 14.1449 12.6196 13.7521 12.4921 13.2655L11.5206 9.55934C11.4932 9.45611 11.4965 9.34749 11.5299 9.24598C11.5634 9.14446 11.6257 9.05416 11.7098 8.98547L14.7249 6.55162C15.1211 6.23183 14.9166 5.59416 14.4076 5.56212L10.47 5.31428C10.364 5.30693 10.2622 5.27052 10.1767 5.20927C10.0911 5.14802 10.0253 5.06447 9.98682 4.96834L8.51826 1.38172C8.47834 1.27969 8.40737 1.19184 8.31475 1.12985C8.22214 1.06787 8.11227 1.03467 7.99974 1.03467C7.88721 1.03467 7.77735 1.06787 7.68473 1.12985C7.59212 1.19184 7.52114 1.27969 7.48122 1.38172L6.01266 4.96834C5.97419 5.06447 5.90834 5.14802 5.8228 5.20927C5.73725 5.27052 5.63553 5.30693 5.52947 5.31428L1.59187 5.56212C1.08287 5.59416 0.878386 6.23183 1.27456 6.55162L4.28972 8.98547C4.37375 9.05416 4.43608 9.14446 4.46954 9.24598C4.503 9.34749 4.50625 9.45611 4.47889 9.55934L3.57797 12.9964C3.42492 13.5803 4.06527 14.0516 4.58102 13.7347L7.70567 11.8148C7.79355 11.7605 7.89557 11.7317 7.99974 11.7317C8.10392 11.7317 8.20593 11.7605 8.29381 11.8148V11.8148Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {imageProject.isFavourite ? "Remove from" : "Add to"} Favorites
        </FavoriteButton>
      </ActionButtonsHolder>
      <ImageToolContentBox imageProject={imageProject} />
      <ImageToolQueriesBox
        title="Stock Libraries Search Queries"
        subtitle="Add queries to generate image results"
        project={imageProject}
        queriesType={ImageQueryType.SEARCH}
        refetch={refetch}
      />
      <ImageToolQueriesBox
        title="Getty Images Search Queries"
        subtitle="Add queries to generate image results"
        project={imageProject}
        queriesType={ImageQueryType.GETTY}
        refetch={refetch}
      />
      <ImageToolQueriesBox
        title="Instagram"
        subtitle="Add queries to generate image results"
        project={imageProject}
        queriesType={ImageQueryType.HASHTAGS}
        refetch={refetch}
      />
      <ImageToolQueriesBox
        title="Midjourney Images"
        subtitle="Add prompts and set parameters to generate image results"
        project={imageProject}
        queriesType={ImageQueryType.PROMPTS}
        refetch={refetch}
      />
      {(!!imageProject.images.library.length ||
        imageProject.statuses.stocks ===
          ImageGenerationProjectStatus.PENDING) && (
        <ImageToolImages
          title="Stock Libraries Results"
          subtitle="Select images to download or generate additional variations with Midjourney"
          type="library"
          project={imageProject}
          selectedImages={selectedStockImages}
          setSelectedImages={setSelectedStockImages}
        />
      )}
      {(!!imageProject.images.getty.length ||
        imageProject.statuses.getty ===
          ImageGenerationProjectStatus.PENDING) && (
        <ImageToolImages
          title="Getty Images Results"
          subtitle="Preview images and reach direct source to make other actions"
          type="getty"
          project={imageProject}
          selectedImages={selectedStockImages}
          setSelectedImages={setSelectedStockImages}
        />
      )}
      {(!!imageProject.images.instagram.length ||
        imageProject.statuses.instagram ===
          ImageGenerationProjectStatus.PENDING) && (
        <ImageToolImages
          title="Instagram"
          subtitle="Select images to download or generate additional variations with Midjourney"
          type="instagram"
          project={imageProject}
          selectedImages={selectedInstagramImages}
          setSelectedImages={setSelectedInstagramImages}
        />
      )}
      {(!!imageProject.images.midjourney.length ||
        imageProject.statuses.midjourney ===
          ImageGenerationProjectStatus.PENDING) && (
        <ImageToolImages
          title="Midjourney Generations"
          subtitle="Select images to download or generate additional variations with Midjourney"
          type="midjourney"
          project={imageProject}
          selectedImages={selectedAiImages}
          setSelectedImages={setSelectedAiImages}
        />
      )}
      <AnimatePresence>
        {(!!selectedStockImages.length ||
          !!selectedAiImages.length ||
          !!selectedInstagramImages.length) && (
          <ImageToolFloatingBox
            selectedStockImages={selectedStockImages}
            selectedAiImages={selectedAiImages}
            selectedInstagramImages={selectedInstagramImages}
            setSelectedStockImages={setSelectedStockImages}
            setSelectedAiImages={setSelectedAiImages}
            setSelectedInstagramImages={setSelectedInstagramImages}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageToolSingle;
